import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Award, HeartHandshake, Truck } from "lucide-react";

const values = [
  {
    icon: ShieldCheck,
    title: "FDA-Approved Quality",
    text: "We provide FDA-approved equipment, vetted against international medical standards before it ever reaches your home.",
  },
  {
    icon: Award,
    title: "Premium Curation",
    text: "We stock only the equipment we would confidently recommend to our own families — nothing less.",
  },
  {
    icon: HeartHandshake,
    title: "Expert Guidance",
    text: "A doctor and a biomedical engineer help you choose the right device — always free of charge.",
  },
  {
    icon: Truck,
    title: "Care That Reaches Everyone",
    text: "From city homes to remote mountain villages, we deliver and set up care wherever you are.",
  },
];

const stats = [
  { num: "10+", label: "Years of Care" },
  { num: "500+", label: "Lives Touched" },
  { num: "2026", label: "Now Online" },
  { num: "100%", label: "Genuine Products" },
];

export default function AboutSection({ refProp }) {
  return (
    <section className="about-section" ref={refProp} id="about">
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="section-eyebrow">WHO WE ARE</div>
          <h2 className="section-title">The MedEase <em>Promise</em></h2>
          <p className="section-sub">
            A decade of care — now just a message away
          </p>
        </motion.div>

        <div className="about-grid">
          <motion.div
            className="about-intro"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="about-lead">
              For over a decade, we have carried quality healthcare into homes
              across Nepal — from busy city apartments to the quiet corners of
              rural villages.
            </p>
            <p className="about-text">
              What began as bedside visits and hand-delivered equipment has grown
              into a promise we keep every single day: that the right care should
              reach everyone, no matter how far the road. We have sat with
              families in remote districts, set up devices where the nearest
              clinic was hours away, and made sure no one felt alone in a moment
              of need.
            </p>
            <p className="about-text">
              We have been doing this work for more than ten years — and since
              2026, we are proud to bring that same warmth, trust, and expertise
              online, so help is now only a message away. Founded by a doctor and
              a biomedical engineer, our mission remains beautifully simple: to
              make premium, life-improving healthcare equipment accessible,
              understandable, and dignified for the people who need it most.
            </p>

            <div className="about-stats">
              {stats.map((s, i) => (
                <motion.div
                  key={s.label}
                  className="about-stat"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 + i * 0.08, duration: 0.6 }}
                >
                  <span className="about-stat-num">{s.num}</span>
                  <span className="about-stat-label">{s.label}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <div className="about-values">
            {values.map((v, i) => {
              const Icon = v.icon;
              return (
                <motion.div
                  key={v.title}
                  className="value-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.7 }}
                >
                  <div className="value-icon-wrap">
                    <Icon size={26} strokeWidth={1.2} />
                  </div>
                  <h3 className="value-title">{v.title}</h3>
                  <p className="value-text">{v.text}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
